// AGC: Polygon Drawing Map - Manual lot definition using map
// AGC: Uses Leaflet for open-source mapping
// AGC: Shows existing lots and prevents overlap

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { MapContainer, TileLayer, Polygon, Marker, useMapEvents, Tooltip, useMap } from 'react-leaflet'
import { X, Trash2, RotateCcw, Check, AlertTriangle, Locate, Plus, Minus } from 'lucide-react'
import { calculatePolygonArea, calculatePolygonPerimeter, isValidPolygon, formatArea, formatPerimeter } from '../../services/polygonService'
import { useLotes, useFarms } from '../../context'
import { useTranslation } from '../../hooks/useTranslation'
import { TopBar } from '../shared'
import { getCropDisplayInfo, type Plot } from '../../types'
import { calculateLotProgress } from '../../utils/lotProgress'
import { useGpsAutoCenter } from '../../hooks/useGpsAutoCenter'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'
import { track } from '../../lib/analytics'

// AGC: Fix Leaflet default marker icon issue
import icon from 'leaflet/dist/images/marker-icon.png'
import iconShadow from 'leaflet/dist/images/marker-shadow.png'

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
})

L.Marker.prototype.options.icon = DefaultIcon

/**
 * AGC: Simple polygon overlap detection using bounding box intersection
 * AGC: For more accurate detection, use turf.js booleanIntersects
 */
const checkPolygonOverlap = (
  newCoords: [number, number][],
  existingPlots: Plot[]
): { overlaps: boolean; overlappingPlots: Plot[] } => {
  if (newCoords.length < 3) return { overlaps: false, overlappingPlots: [] }
  
  const overlappingPlots: Plot[] = []
  
  // AGC: Get bounding box of new polygon
  const newBbox = getBoundingBox(newCoords)
  
  for (const plot of existingPlots) {
    if (!plot.coordinates || plot.coordinates.length < 3) continue
    
    // AGC: Get bounding box of existing plot
    const plotBbox = getBoundingBox(plot.coordinates)
    
    // AGC: Check if bounding boxes intersect (potential overlap)
    if (bboxIntersects(newBbox, plotBbox)) {
      // AGC: More detailed check - see if any point of new polygon is inside existing
      // AGC: or vice versa (simple containment check)
      const hasContainment = 
        newCoords.some(point => isPointInPolygon(point, plot.coordinates)) ||
        plot.coordinates.some(point => isPointInPolygon(point, newCoords))
      
      if (hasContainment) {
        overlappingPlots.push(plot)
      }
    }
  }
  
  return {
    overlaps: overlappingPlots.length > 0,
    overlappingPlots,
  }
}

/**
 * AGC: Get bounding box of a polygon
 */
const getBoundingBox = (coords: [number, number][]) => {
  let minLat = Infinity, maxLat = -Infinity
  let minLng = Infinity, maxLng = -Infinity
  
  for (const [lat, lng] of coords) {
    minLat = Math.min(minLat, lat)
    maxLat = Math.max(maxLat, lat)
    minLng = Math.min(minLng, lng)
    maxLng = Math.max(maxLng, lng)
  }
  
  return { minLat, maxLat, minLng, maxLng }
}

/**
 * AGC: Check if two bounding boxes intersect
 */
const bboxIntersects = (
  bbox1: { minLat: number; maxLat: number; minLng: number; maxLng: number },
  bbox2: { minLat: number; maxLat: number; minLng: number; maxLng: number }
) => {
  return !(
    bbox1.maxLat < bbox2.minLat ||
    bbox1.minLat > bbox2.maxLat ||
    bbox1.maxLng < bbox2.minLng ||
    bbox1.minLng > bbox2.maxLng
  )
}

/**
 * AGC: Ray-casting algorithm to check if a point is inside a polygon
 */
const isPointInPolygon = (point: [number, number], polygon: [number, number][]): boolean => {
  const [x, y] = point
  let inside = false
  
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i]
    const [xj, yj] = polygon[j]
    
    if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
      inside = !inside
    }
  }
  
  return inside
}

interface PolygonDrawingMapProps {
  lotName?: string // AGC: Optional lot name passed from NovoLote
  onCancel: () => void
  onSave: (polygon: { name?: string; coordinates: [number, number][]; area: number; perimeter: number }) => void
}

/**
 * AGC: Map click handler component
 */
interface MapClickHandlerProps {
  onMapClick: (lat: number, lng: number) => void
}

const MapClickHandler: React.FC<MapClickHandlerProps> = ({ onMapClick }) => {
  useMapEvents({
    click: (e) => {
      onMapClick(e.latlng.lat, e.latlng.lng)
    },
  })
  return null
}

/**
 * AGC: Calculate polygon centroid for overlay placement
 */
const calculateCentroid = (coordinates: [number, number][]): [number, number] => {
  if (!coordinates || coordinates.length === 0) return [0, 0]
  
  let sumLat = 0
  let sumLng = 0
  
  coordinates.forEach(([lat, lng]) => {
    sumLat += lat
    sumLng += lng
  })
  
  return [sumLat / coordinates.length, sumLng / coordinates.length]
}

/**
 * AGC: MapBoundsFitter - Fits map to show existing lots on initial load
 * AGC: Handles 0, 1, or 2+ lots scenarios for proper initial zoom
 */
interface MapBoundsFitterProps {
  existingLots: Plot[]
  farmCenter?: [number, number]
  enabled: boolean // AGC: Only run once on initial load
}

const MapBoundsFitter: React.FC<MapBoundsFitterProps> = ({ existingLots, farmCenter, enabled }) => {
  const map = useMap()
  
  useEffect(() => {
    if (!enabled) return
    
    // AGC: Scenario 1: No existing lots
    if (existingLots.length === 0) {
      // AGC: Use farm center if available, otherwise default
      if (farmCenter) {
        map.setView(farmCenter, 15)
      }
      return
    }
    
    // AGC: Calculate bounding box from all lot coordinates
    let minLat = Infinity
    let maxLat = -Infinity
    let minLng = Infinity
    let maxLng = -Infinity
    
    existingLots.forEach(lot => {
      if (lot.coordinates && lot.coordinates.length >= 3) {
        lot.coordinates.forEach(([lat, lng]) => {
          minLat = Math.min(minLat, lat)
          maxLat = Math.max(maxLat, lat)
          minLng = Math.min(minLng, lng)
          maxLng = Math.max(maxLng, lng)
        })
      }
    })
    
    // AGC: Only fit bounds if we have valid coordinates
    if (minLat !== Infinity && maxLat !== -Infinity && minLng !== Infinity && maxLng !== -Infinity) {
      // AGC: Add padding for better UX
      const padding = existingLots.length === 1 ? [80, 80] : [60, 60]
      const maxZoom = existingLots.length === 1 ? 17 : 16
      
      map.fitBounds(
        [[minLat, minLng], [maxLat, maxLng]],
        { padding: padding as [number, number], maxZoom }
      )
    }
  }, [existingLots, farmCenter, enabled, map])
  
  return null
}

/**
 * AGC: GpsAutoCenterControl - Inner Leaflet child that auto-centers the map
 * AGC: on the user's current GPS location once on mount, but only when the
 * AGC: page has no existing lots and no farm coordinates to anchor to.
 * AGC: Native Android/iOS uses internal GPS; web prompts for browser
 * AGC: geolocation permission.
 */
interface GpsAutoCenterControlProps {
  enabled: boolean
}

const GpsAutoCenterControl: React.FC<GpsAutoCenterControlProps> = ({ enabled }) => {
  const map = useMap()
  useGpsAutoCenter({
    enabled,
    onLocate: (coords) => {
      map.setView([coords.latitude, coords.longitude], 16)
    },
  })
  return null
}

/**
 * AGC: MapRefCapture - captures the Leaflet map instance into an external ref.
 * AGC: Must be rendered inside MapContainer to access useMap().
 */
const MapRefCapture: React.FC<{ mapRef: React.MutableRefObject<L.Map | null> }> = ({ mapRef }) => {
  const map = useMap()
  useEffect(() => { mapRef.current = map }, [map, mapRef])
  return null
}

/**
 * AGC: Create custom HTML icon for existing LOT overlay
 * AGC: Progress is now DYNAMICALLY calculated based on planting date and crop cycle
 */
const createExistingLotOverlayIcon = (
  lot: Plot,
  cropDisplay: { icon: string; name: string; color: string },
  progress: number // AGC: Pre-calculated progress from calculateLotProgress()
): L.DivIcon => {
  const areaText = formatArea(lot.area || 0)
  const progressColor = progress >= 80 ? '#10B981' : progress >= 50 ? '#F59E0B' : '#6B7280'
  
  const html = `
    <div style="
      background: rgba(255, 255, 255, 0.9);
      backdrop-filter: blur(4px);
      border-radius: 6px;
      padding: 4px 8px;
      box-shadow: 0 2px 6px rgba(0,0,0,0.2);
      border: 2px solid ${cropDisplay.color};
      min-width: 70px;
      text-align: center;
      transform: translate(-50%, -50%);
      font-family: system-ui, -apple-system, sans-serif;
      pointer-events: none;
    ">
      <div style="display: flex; align-items: center; justify-content: center; gap: 3px; margin-bottom: 1px;">
        <span style="font-size: 14px;">${cropDisplay.icon}</span>
        <span style="font-size: 10px; font-weight: 600; color: #374151; max-width: 50px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
          ${cropDisplay.name}
        </span>
      </div>
      <div style="font-size: 11px; font-weight: 700; color: #1F2937; margin-bottom: 2px;">
        ${areaText}
      </div>
      <div style="background: #E5E7EB; border-radius: 3px; height: 3px; overflow: hidden;">
        <div style="background: ${progressColor}; width: ${progress}%; height: 100%; border-radius: 3px;"></div>
      </div>
    </div>
  `
  
  return L.divIcon({
    className: 'existing-lot-overlay-marker',
    html: html,
    iconSize: [90, 50],
    iconAnchor: [45, 25],
  })
}

/**
 * AGC: PolygonDrawingMap - Manual polygon definition on map
 * AGC: Shows existing lots in the farm and prevents overlap
 */
export const PolygonDrawingMap: React.FC<PolygonDrawingMapProps> = ({ lotName, onCancel, onSave }) => {
  const { t } = useTranslation()
  const { plots, getActivePlotsForField } = useLotes()
  const { currentFarm } = useFarms()
  
  // AGC: Calculate initial center - use farm coordinates if available, otherwise default to Brasília
  const center: [number, number] = useMemo(() => {
    if (currentFarm?.coordinates) {
      return currentFarm.coordinates
    }
    // AGC: Default to Brasília region
    return [-15.7975, -47.8919]
  }, [currentFarm])
  
  const [vertices, setVertices] = useState<[number, number][]>([])
  const [isPreviewMode, setIsPreviewMode] = useState(false)
  const [calculatedData, setCalculatedData] = useState<{ area: number; perimeter: number } | null>(null)
  const [overlapError, setOverlapError] = useState<{ overlaps: boolean; plots: Plot[] } | null>(null)
  const [hasFittedBounds, setHasFittedBounds] = useState(false) // AGC: Track if initial fit has occurred
  const [isLocating, setIsLocating] = useState(false)
  // AGC: mapRef captures the Leaflet map instance from inside MapContainer via MapRefCapture
  const mapRef = useRef<L.Map | null>(null)
  
  // AGC: Get existing active lots for current farm (to show on map and prevent overlap)
  const existingLots = useMemo(() => {
    if (!currentFarm) return []
    // AGC: Get only active (non-archived) plots with valid coordinates
    return plots.filter(p => 
      p.fieldId === currentFarm.id && 
      p.coordinates && 
      p.coordinates.length >= 3 &&
      !p.isArchived
    )
  }, [plots, currentFarm])
  
  // AGC: Mark bounds as fitted once existingLots are loaded
  useEffect(() => {
    if (!hasFittedBounds && (existingLots.length > 0 || currentFarm)) {
      // AGC: Small delay to ensure map is ready
      const timer = setTimeout(() => setHasFittedBounds(true), 100)
      return () => clearTimeout(timer)
    }
  }, [existingLots, currentFarm, hasFittedBounds])

  // AGC: Add vertex on map click
  const handleMapClick = useCallback((lat: number, lng: number) => {
    if (!isPreviewMode) {
      setVertices(prev => [...prev, [lat, lng]])
    }
  }, [isPreviewMode])

  // AGC: Remove last vertex
  const handleRemoveLastVertex = () => {
    if (vertices.length > 0) {
      setVertices(prev => prev.slice(0, -1))
    }
  }

  // AGC: Clear all vertices
  const handleClear = () => {
    setVertices([])
    setIsPreviewMode(false)
    setCalculatedData(null)
    setOverlapError(null) // AGC: Also clear overlap error
  }

  // AGC: Complete polygon and show preview
  const handleComplete = () => {
    if (!isValidPolygon(vertices)) {
      alert('É necessário pelo menos 3 pontos para criar um polígono.')
      return
    }

    // AGC: Check for overlap with existing lots
    const overlap = checkPolygonOverlap(vertices, existingLots)
    if (overlap.overlaps) {
      setOverlapError({ overlaps: true, plots: overlap.overlappingPlots })
      return
    }
    setOverlapError(null)

    const area = calculatePolygonArea(vertices)
    const perimeter = calculatePolygonPerimeter(vertices)

    setCalculatedData({ area, perimeter })
    setIsPreviewMode(true)
  }

  // AGC: Save polygon
  const handleSave = () => {
    if (calculatedData) {
      // AGC: Telemetry — user finalized a manually-drawn polygon
      void track('feature_used', { feature_name: 'map_draw' })
      onSave({
        name: lotName,
        coordinates: vertices,
        area: calculatedData.area,
        perimeter: calculatedData.perimeter,
      })
    }
  }

  // AGC: Back to editing mode
  const handleBackToEdit = () => {
    setIsPreviewMode(false)
    setCalculatedData(null)
  }

  // AGC: Zoom handlers — delegate to Leaflet map instance via mapRef
  const handleZoomIn = useCallback(() => { mapRef.current?.zoomIn() }, [])
  const handleZoomOut = useCallback(() => { mapRef.current?.zoomOut() }, [])

  // AGC: Go-to-my-location handler — gets device GPS and pans the map there
  const handleLocate = useCallback(async () => {
    setIsLocating(true)
    try {
      const { getCurrentPosition } = await import('../../services/nativeGeolocation')
      const coords = await getCurrentPosition({ enableHighAccuracy: true, timeout: 10000 })
      mapRef.current?.setView([coords.latitude, coords.longitude], Math.max(mapRef.current.getZoom(), 16))
    } catch {
      // AGC: Silent fail — GPS unavailable or denied; button can be tapped again
    } finally {
      setIsLocating(false)
    }
  }, [])

  return (
    // AGC: Standardized "make new field" map page layout — full-screen flex
    // AGC: column with the shared TopBar at the top so the back button is
    // AGC: always visible in the same place across every map flow.
    <div className="fixed inset-0 z-[90] bg-gray-900 flex flex-col">
      {/* AGC: Shared TopBar — replaces the previous translucent overlay header
          which left this page with no visible back button on Android (the
          tiny white-on-dark "X Cancelar" was effectively invisible against
          the system status bar). */}
      <div className="flex-shrink-0">
        <TopBar
          title={t('fields.drawLot')}
          onBack={onCancel}
          noSafeAreaTop={false}
        />
      </div>

      {/* AGC: Map fills the remaining vertical space below the TopBar.
          All overlay controls (existing-lots indicator, overlap alert,
          bottom controls, preview panel) are now positioned absolutely
          relative to THIS container, not the viewport, so they no longer
          collide with the TopBar. */}
      <div className="flex-1 relative">
      {/* AGC: Map */}
      <MapContainer
        center={center}
        zoom={15}
        style={{ height: '100%', width: '100%' }}
        zoomControl={false}
      >
        {/* AGC: Satellite/Terrain view using ESRI World Imagery */}
        <TileLayer
          attribution='Tiles &copy; Esri'
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          maxZoom={19}
        />

        {/* AGC: Capture Leaflet map instance for use outside MapContainer */}
        <MapRefCapture mapRef={mapRef} />

        {/* AGC: Auto-fit bounds to existing lots on initial load */}
        <MapBoundsFitter
          existingLots={existingLots}
          farmCenter={currentFarm?.coordinates}
          enabled={hasFittedBounds}
        />

        {/* AGC: Auto-center on user's current device GPS when there is no farm
            coordinate and no existing lots to anchor on. The hardcoded
            Brasília fallback above is replaced at runtime by the user's
            actual location (native GPS on app, browser permission on web). */}
        <GpsAutoCenterControl
          enabled={!currentFarm?.coordinates && existingLots.length === 0}
        />

        {/* AGC: Map click handler */}
        <MapClickHandler onMapClick={handleMapClick} />

        {/* AGC: Existing lots (non-editable reference polygons) with crop info overlay */}
        {existingLots.map((lot) => {
          const cropDisplay = getCropDisplayInfo(lot.cropType)
          const centroid = calculateCentroid(lot.coordinates)
          // AGC: Calculate REAL progress based on planting date and crop cycle
          const progress = calculateLotProgress(lot)
          const overlayIcon = createExistingLotOverlayIcon(lot, cropDisplay, progress)
          
          return (
            <React.Fragment key={lot.id}>
              <Polygon
                positions={lot.coordinates}
                pathOptions={{
                  color: cropDisplay.color, // AGC: Use crop color instead of gray
                  fillColor: cropDisplay.color,
                  fillOpacity: 0.25,
                  weight: 2,
                  dashArray: '5, 5', // AGC: Dashed border to distinguish from new polygon
                }}
              />
              {/* AGC: Overlay marker showing crop icon, area, and progress */}
              <Marker
                position={centroid}
                icon={overlayIcon}
                interactive={false} // AGC: Don't interfere with polygon click
              />
            </React.Fragment>
          )
        })}

        {/* AGC: Markers for vertices */}
        {vertices.map((vertex, idx) => (
          <Marker key={idx} position={vertex} />
        ))}

        {/* AGC: New polygon being drawn */}
        {vertices.length >= 3 && (
          <Polygon
            positions={vertices}
            pathOptions={{
              color: overlapError ? '#EF4444' : '#F59E0B', // AGC: Red if overlap, amber otherwise
              fillColor: overlapError ? '#FCA5A5' : '#FCD34D', // AGC: Red tint if overlap
              fillOpacity: 0.3,
              weight: 3,
            }}
          />
        )}
      </MapContainer>

      {/* AGC: Right-side control column — GPS locate button + zoom in/out stacked below.
          All three live in the same absolute group so they never drift apart. */}
      <div className="absolute top-2 right-2 z-[1001] flex flex-col items-center gap-1">
        {/* AGC: GPS locate button */}
        <button
          onClick={handleLocate}
          disabled={isLocating}
          className="w-10 h-10 bg-white rounded-full shadow-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Minha localização"
        >
          {isLocating ? (
            <span className="animate-spin text-base">⏳</span>
          ) : (
            <Locate className="w-5 h-5 text-[#0B5F4A]" />
          )}
        </button>
        {/* AGC: Zoom in */}
        <button
          onClick={handleZoomIn}
          className="w-10 h-10 bg-white rounded-t-lg shadow-md border border-gray-200 flex items-center justify-center hover:bg-gray-50 active:bg-gray-100 text-gray-700 font-bold"
          title="Zoom in"
        >
          <Plus className="w-5 h-5" />
        </button>
        {/* AGC: Zoom out */}
        <button
          onClick={handleZoomOut}
          className="w-10 h-10 bg-white rounded-b-lg shadow-md border border-t-0 border-gray-200 flex items-center justify-center hover:bg-gray-50 active:bg-gray-100 text-gray-700 font-bold -mt-1"
          title="Zoom out"
        >
          <Minus className="w-5 h-5" />
        </button>
      </div>

      {/* AGC: Preview Panel (after "Concluir") */}
      {isPreviewMode && calculatedData && (
        <div className="absolute bottom-20 left-4 right-4 z-[1000] bg-white rounded-lg shadow-xl p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {lotName || 'Lote definido'}
          </h2>
          
          <div className="space-y-3 mb-6">
            <div className="flex justify-between">
              <span className="text-gray-600">Área:</span>
              <span className="font-semibold text-gray-900">{formatArea(calculatedData.area)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Perímetro:</span>
              <span className="font-semibold text-gray-900">{formatPerimeter(calculatedData.perimeter)}</span>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleBackToEdit}
              className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
            >
              Voltar
            </button>
            <button
              onClick={handleSave}
              className="flex-1 px-4 py-3 bg-[#0B5F4A] rounded-lg text-white font-medium hover:bg-[#094935]"
            >
              Salvar lote
            </button>
          </div>
        </div>
      )}

      {/* AGC: Existing lots indicator — left-anchored with max-width so it never
          stretches over the right-side locate button or Leaflet zoom controls. */}
      {existingLots.length > 0 && !isPreviewMode && (
        <div className="absolute top-2 left-4 z-[1000] max-w-[calc(100%-4.5rem)]">
          <div className="bg-gray-800/80 backdrop-blur rounded-lg px-3 py-2 shadow-lg">
            <p className="text-xs text-gray-200 truncate">
              📍 {t('fields.existingLots')}: {existingLots.length} ({existingLots.map(l => l.name).join(', ')})
            </p>
          </div>
        </div>
      )}

      {/* AGC: Overlap Error Alert — was top-28, now top-14 to sit just below
          the existing-lots pill inside the flex-1 wrapper. */}
      {overlapError && overlapError.overlaps && (
        <div className="absolute top-14 left-4 right-14 z-[1000]">
          <div className="bg-red-500/95 backdrop-blur rounded-lg p-4 shadow-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-white flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-white">
                  {t('fields.polygonOverlap')}
                </p>
                <p className="text-xs text-red-100 mt-1">
                  {t('fields.polygonOverlapDescription')}
                </p>
                <p className="text-xs text-red-200 mt-1">
                  {overlapError.plots.map(p => p.name).join(', ')}
                </p>
              </div>
              <button
                onClick={() => setOverlapError(null)}
                className="text-white/80 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AGC: Bottom Controls (editing mode) */}
      {!isPreviewMode && (
        <div className="absolute bottom-4 left-4 right-4 z-[1000]">
          {/* AGC: Help text */}
          {vertices.length === 0 && !overlapError && (
            <div className="mb-3 bg-blue-500/90 backdrop-blur rounded-lg p-3 shadow-lg">
              <p className="text-xs text-white text-center">
                📍 Toque no mapa para adicionar pontos e criar o polígono
              </p>
            </div>
          )}
          
          <div className="flex gap-2">
            <button
              onClick={handleRemoveLastVertex}
              disabled={vertices.length === 0}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-3 bg-white/90 backdrop-blur rounded-lg text-gray-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              <RotateCcw className="w-4 h-4" />
              <span className="hidden sm:inline">Apagar</span>
            </button>

            <button
              onClick={handleClear}
              disabled={vertices.length === 0}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-3 bg-white/90 backdrop-blur rounded-lg text-red-600 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              <Trash2 className="w-4 h-4" />
              <span className="hidden sm:inline">Limpar</span>
            </button>

            <button
              onClick={handleComplete}
              disabled={vertices.length < 3}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-3 bg-[#0B5F4A] rounded-lg text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              <Check className="w-4 h-4" />
              Concluir
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  )
}

export default PolygonDrawingMap

